import Icon from '@ant-design/icons';
import { Image } from 'antd';
import classnames from 'classnames';
import type { MouseEvent } from 'react';
import { memo, useCallback } from 'react';

import PPTSvg from '@/components/Icon/svgs/PPTX.svg';
import ExlSvg from '@/components/Icon/svgs/exl_file.svg';
import PdfSvg from '@/components/Icon/svgs/pdf_file.svg';
import TxtSvg from '@/components/Icon/svgs/text_file.svg';
import UnknownSvg from '@/components/Icon/svgs/unknown_file.svg';
import VoiceSvg from '@/components/Icon/svgs/voice_file.svg';
import WordSvg from '@/components/Icon/svgs/word_file.svg';
// import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
// import useStorage from '@/hooks/useStorage';
import type { ReactClassNameInterface } from '@/types/common';
import { isUrl } from '@/utils/utils';

import { getShowFileType } from '../../utils/utils';
import { ChatListElementContainerClassName } from '../chat/usePopover';
import SvgClose from '../svg/close';
import SvgLoading from '../svg/loading';
import styles from './index.less';

interface IProps extends ReactClassNameInterface {
  id?: string;
  name: string;
  type: string;
  link: string;
  imgClassName?: string;
  fileClassName?: string;
  loading?: boolean;
  onDel?(id: string): void;
}

const imgBg =
  'https://frontdoorcdn.mindverse.ai/mindos-resource/front-img/img/zzzz202306081686229285503imgbg.png';

const PdfIcon = {
  svg: PdfSvg,
  color: '#CD6B6B'
};

const WordIcon = {
  svg: WordSvg,
  color: '#5DA5CE'
};

const TxtIcon = {
  svg: TxtSvg,
  color: '#6A85B6'
};

const ExlIcon = {
  svg: ExlSvg,
  color: '#58AF6C'
};

const OtherIcon = {
  svg: UnknownSvg,
  color: '#909090'
};

const VoiceIcon = {
  svg: VoiceSvg,
  color: '#806ED9'
};

const PPTIcon = {
  svg: PPTSvg,
  color: '#D87A45'
};

const FileIconMap: { [key: string]: { svg: any; color: string } | undefined } = {
  'application/pdf': PdfIcon,
  'application/msword': WordIcon,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': WordIcon,
  'application/vnd.ms-powerpoint': PPTIcon,
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': PPTIcon,
  'text/plain': TxtIcon,
  'application/vnd.ms-excel': ExlIcon,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ExlIcon,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template': ExlIcon,
  'application/vnd.ms-excel.sheet.macroEnabled.12': ExlIcon,
  'application/vnd.ms-excel.template.macroEnabled.12': ExlIcon,
  'application/vnd.ms-excel.addin.macroEnabled.12': ExlIcon,
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12': ExlIcon,
  'text/csv': ExlIcon,
  'audio/wav': VoiceIcon,
  'audio/vnd.wave': VoiceIcon,
  'audio/wave': VoiceIcon,
  'audio/mpeg': VoiceIcon,
  'audio/m4a': VoiceIcon,
  'audio/x-m4a': VoiceIcon,
  'audio/aac': VoiceIcon,
  'audio/mp4': VoiceIcon,
  'video/mp4': VoiceIcon
};

export const getFileIcon = (type: string): { svg: any; color: string } => {
  return FileIconMap[type] || OtherIcon;
};

function FileCard(props: IProps): JSX.Element {
  const {
    id,
    name,
    type,
    link,
    loading,
    onDel,
    className = '',
    fileClassName = '',
    imgClassName = ''
  } = props;

  // const [layoutSize] = useStorage('layoutSize');
  // const [inMobile, setInMobile] = useState(layoutSize === LAYOUT_SIZE.SMALL);

  // useEffect(() => {
  //   setInMobile(layoutSize === LAYOUT_SIZE.SMALL);
  // }, [layoutSize]);

  const handleCloseClick = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onDel?.(id!);
    },
    [onDel, id]
  );

  const handleCardClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!link) {
        e.preventDefault();
      }

      window.open(link, '_blank');
    },
    [link]
  );

  if (type.startsWith('image/')) {
    return (
      <div
        className={classnames(
          styles.fileCard,
          isUrl(link) ? 'cursor-pointer' : '',
          className,
          imgClassName,
          onDel ? styles.imgCardInput : styles.imgCard
        )}
        onClick={handleCardClick}
      >
        <Image alt="" className={styles.img} preview={false} src={link || imgBg} />
        {onDel ? (
          loading ? (
            <SvgLoading
              className={classnames(styles.controlIcon, styles.loadingIcon)}
              color="#fff"
              height="16"
              width="16"
            />
          ) : (
            <SvgClose
              className={styles.controlIcon}
              color="#fff"
              height="16"
              onClick={handleCloseClick}
              width="16"
            />
          )
        ) : null}
      </div>
    );
  }

  const FileIcon = FileIconMap[type] || OtherIcon;

  return (
    <div
      className={classnames(
        styles.fileCard,
        className,
        ChatListElementContainerClassName,
        fileClassName,
        onDel ? styles.fileCardInput : ''
      )}
      data-link={link}
      onClick={handleCardClick}
    >
      <Icon
        className={styles.fileIcon}
        component={FileIcon.svg}
        style={{ color: FileIcon.color }}
      />
      {onDel ? (
        loading ? (
          <SvgLoading
            className={classnames(styles.controlIcon, styles.loadingIcon)}
            color="#fff"
            height="16"
            width="16"
          />
        ) : (
          <SvgClose
            className={classnames(styles.controlIcon, styles.closeIcon)}
            color="#fff"
            height="16"
            onClick={handleCloseClick}
            width="16"
          />
        )
      ) : null}
      <div className="flex flex-col ml-4 gap-[2px] text-ellipsis overflow-hidden break-all">
        <div className={styles.name}>{name}</div>
        <div className={styles.type}>{getShowFileType(link)}</div>
      </div>
    </div>
  );
}

export default memo(FileCard);
